import { AgentType, AgentTypeName, HomeType, PlanType } from '@/enums';
import { getAPIPath } from '@/helpers/APIHelper';
import { getCountryCode } from '@/helpers/CountryCodeHelper';
import { getLppsaPlanClass } from '@/helpers/LppsaPlanClassHelper';
import { store } from '@/store';
import {
  CalculateMyRumahPlanPriceRequest,
  CalculatePlanPriceRequest,
} from '@/types/Proposal/CalculatePlanPriceRequest';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { format, parseISO } from 'date-fns';
import axiosInterceptorInstance from './axiosInterceptorInstance';

export const calculatePlanPrice = createAsyncThunk(
  'quote/CalculatePremium',
  async ({
    productId,
    productCode,
    language,
  }: {
    productId: number;
    productCode: string;
    language: string;
  }) => {
    try {
      const rootState = store.getState();
      const planSummary = rootState.planSummary;
      const uiState = rootState.uiState;
      const infoState = rootState.infoState;
      const isLppsa = !!uiState.isLppsa;
      const affiliateState = rootState.affiliate;

      let agentType;
      switch (affiliateState.type) {
        case AgentTypeName.Agent:
          agentType = affiliateState.isDirectAgent
            ? AgentType.DirectAgent
            : AgentType.IndirectAgent;
          break;
        case AgentTypeName.Banca:
          agentType = affiliateState.isValidStaff
            ? AgentType.BancaStaff
            : AgentType.BancaNonStaff;
          break;
        default:
          agentType = AgentType.DirectAgent;
      }

      const baseRequest = {
        quotationNo: planSummary.planPrice?.quotationNo || '',
        startDate: format(
          parseISO(planSummary.quotationDetail.startDate),
          'dd/MM/yyyy',
        ),
        countryCode: getCountryCode(),
        productId,
        productCode,
        buildingType: planSummary.quotationDetail.buildingType,
        constructionType: planSummary.quotationDetail.constructionType,
        agentCode: affiliateState.agentCode || '', // backend handles default e-channel agent code
        agentType,
        isStaff: affiliateState.isValidStaff,
        isMAEM2U: affiliateState.isMAEM2U ?? false,
        language,
      };

      const hohhRequest: CalculatePlanPriceRequest = {
        ...baseRequest,
        isLppsa,
        planType: isLppsa ? PlanType.ContentProtection : planSummary.quotationDetail.planType,
        planClass: isLppsa ? getLppsaPlanClass(planSummary.quotationDetail.planType) : undefined,
        ownershipType: planSummary.quotationDetail.ownership,
        buildingSumInsured: planSummary.quotationDetail.buildingSumInsured,
        contentSumInsured: planSummary.quotationDetail.contentSumInsured,
        addOnCode: planSummary.addOn,
        identityNumber: infoState.personalDetail.idNumber.toString(),
        identityType: infoState.personalDetail.idType,
        countryIsoCode: uiState.nationalityList.find(
          (x) => x.nationalityId === infoState.personalDetail.nationality,
        )?.isO3Code,
      };

      const myRumahRequest: CalculateMyRumahPlanPriceRequest = {
        ...baseRequest,
        identityNumber: infoState.personalDetail.idNumber.toString(),
        identityType: infoState.personalDetail.idType,
        countryIsoCode: uiState.nationalityList.find(
          (x) => x.nationalityId === infoState.personalDetail.nationality,
        )?.isO3Code,
      };

      const relativeUrl = `${getAPIPath()}/CalculatePremium`;
      const apiUrl = `/quote/?relativeUrl=${relativeUrl}`;
      const response = await axiosInterceptorInstance.post(
        apiUrl,
        uiState.homeType === HomeType.Hohh ? hohhRequest : myRumahRequest,
      );
      return response.data;
    } catch (error) {
      return error;
    }
  },
);

export const getProductStatus = createAsyncThunk(
  'productStatus',
  async ({ productCode }: { productCode: string }) => {
    try {
      const relativeUrl = 'product/getstatus/';
      const apiUrl = `/product/?relativeUrl=${relativeUrl}&queryString=${productCode}`;
      const response = await axiosInterceptorInstance.get(apiUrl);
      return response.data;
    } catch (error) {
      return error;
    }
  },
);
